import React, { useState, useEffect } from 'react';
import { API_URL } from '../lib/api';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getCoachMyPlayers, getCoachTeams } from '../lib/api';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { 
  Users, 
  ChevronRight,
  TrendingUp,
  Trophy,
  Target,
  Loader2,
  UserPlus,
  Calendar,
  Plus,
  Activity,
  Percent,
  Medal,
  FileText,
  ChevronDown,
  BarChart3,
  ExternalLink,
  Rocket,
  Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';


const CoachDashboard = () => {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null); // null = All Seasons
  
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamSeasons(selectedTeam.id);
      fetchTeamStats(selectedTeam.id, selectedSeasonId);
    }
  }, [selectedTeam]);
  
  useEffect(() => {
    if (selectedTeam) {
      fetchTeamStats(selectedTeam.id, selectedSeasonId);
    }
  }, [selectedSeasonId]);
  
  const fetchTeamSeasons = async (teamId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/coach/teams/${teamId}/seasons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeasons(res.data.seasons || []);
    } catch (error) {
      console.error('Failed to fetch seasons:', error);
      setSeasons([]);
    }
  };
  
  const fetchData = async () => {
    try {
      const [playersRes, teamsRes] = await Promise.all([
        getCoachMyPlayers().catch(() => ({ data: { players: [] } })),
        getCoachTeams().catch(() => ({ data: { teams: [] } }))
      ]);
      
      setPlayers(playersRes.data.players || []);
      const teamList = teamsRes.data.teams || [];
      setTeams(teamList);
      
      // Auto-select first team if available
      if (teamList.length > 0 && !selectedTeam) {
        setSelectedTeam(teamList[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamStats = async (teamId, seasonId = null) => {
    try {
      const token = localStorage.getItem('token');
      const url = seasonId 
        ? `${API_URL}/coach/teams/${teamId}/stats?season_id=${seasonId}`
        : `${API_URL}/coach/teams/${teamId}/stats`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamStats(res.data);
    } catch (error) {
      console.error('Failed to fetch team stats:', error);
      // Mock stats for display
      setTeamStats({
        record: { wins: 0, losses: 0 },
        avg_points: 0,
        avg_rebounds: 0,
        avg_assists: 0,
        games_played: 0,
        recent_games: [],
        player_stats: []
      });
    }
  };
  
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </Layout>
    );
  }
  
  const totalPlayers = players.length;
  const totalTeams = teams.length;
  const activePlayers = players.filter(p => p.shot_count > 0 || p.game_count > 0).length;

  // Sort players by activity
  const topPlayers = [...players]
    .sort((a, b) => ((b.shot_count || 0) + (b.game_count || 0)) - ((a.shot_count || 0) + (a.game_count || 0)))
    .slice(0, 5);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="coach-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-muted-foreground">{getGreeting()}</p>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight uppercase text-foreground">
              Coach {user?.name?.split(' ')[0] || ''}
            </h1>
          </div>
          
          <div className="flex gap-3">
            <Link to="/coach/teams">
              <Button variant="outline" className="gap-2">
                <Trophy size={18} />
                Manage Teams
              </Button>
            </Link>
            <Link to="/coach/my-players">
              <Button className="gap-2 bg-primary">
                <Users size={18} />
                Roster
              </Button>
            </Link>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Trophy size={16} />
              <span className="text-xs font-medium uppercase">Teams</span>
            </div>
            <p className="font-heading text-3xl font-black text-foreground">{totalTeams}</p>
          </div>
          
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Users size={16} />
              <span className="text-xs font-medium uppercase">Players</span>
            </div>
            <p className="font-heading text-3xl font-black text-foreground">{totalPlayers}</p>
          </div>
          
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Activity size={16} />
              <span className="text-xs font-medium uppercase">Active</span>
            </div>
            <p className="font-heading text-3xl font-black text-primary">{activePlayers}</p>
          </div>
          
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Calendar size={16} />
              <span className="text-xs font-medium uppercase">Games</span>
            </div>
            <p className="font-heading text-3xl font-black text-foreground">
              {players.reduce((sum, p) => sum + (p.game_count || 0), 0)}
            </p>
          </div>
        </div>

        {/* Team Selector & Analytics */}
        {totalTeams > 0 ? (
          <div className="space-y-4">
            {/* Team Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {teams.map((team) => (
                <button
                  key={team.id}
                  onClick={() => setSelectedTeam(team)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-colors ${
                    selectedTeam?.id === team.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border text-foreground hover:border-primary/50'
                  }`}
                >
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: team.color || '#FFFFFF' }} 
                  />
                  {team.name}
                </button>
              ))}
              <Link to="/coach/teams">
                <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium bg-muted text-muted-foreground hover:text-foreground transition-colors">
                  <Plus size={16} />
                  Add Team
                </button>
              </Link>
            </div>

            {/* Selected Team Analytics */}
            {selectedTeam && (
              <div className="space-y-4">
                {/* Team Header */}
                <div className="bg-card border border-border rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${selectedTeam.color || '#FFFFFF'}20` }}
                      >
                        <Trophy size={24} style={{ color: selectedTeam.color || '#FFFFFF' }} />
                      </div>
                      <div>
                        <h2 className="font-heading text-2xl font-bold text-foreground">{selectedTeam.name}</h2>
                        <p className="text-sm text-muted-foreground">
                          {teamStats?.player_count || 0} players • {teamStats?.games_played || 0} games
                        </p>
                      </div>
                    </div>
                    <Link to={`/coach/teams/${selectedTeam.id}/game`}>
                      <Button className="gap-2 bg-primary">
                        <Plus size={18} />
                        Log Team Game
                      </Button>
                    </Link>
                  </div>

                  {/* Team Record & Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-muted rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase mb-1">Record</p>
                      <p className="font-heading text-2xl font-black text-foreground">
                        {teamStats?.record?.wins || 0}-{teamStats?.record?.losses || 0}
                      </p>
                    </div>
                    <div className="bg-muted rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase mb-1">PPG</p>
                      <p className="font-heading text-2xl font-black text-foreground">
                        {teamStats?.avg_points?.toFixed(1) || 0}
                      </p>
                    </div>
                    <div className="bg-muted rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase mb-1">RPG</p>
                      <p className="font-heading text-2xl font-black text-foreground">
                        {teamStats?.avg_rebounds?.toFixed(1) || 0}
                      </p>
                    </div>
                    <div className="bg-muted rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase mb-1">APG</p>
                      <p className="font-heading text-2xl font-black text-foreground">
                        {teamStats?.avg_assists?.toFixed(1) || 0}
                      </p>
                    </div>
                    <div className="bg-muted rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase mb-1">Win %</p>
                      <p className="font-heading text-2xl font-black text-primary">
                        {teamStats?.record?.wins + teamStats?.record?.losses > 0 
                          ? ((teamStats?.record?.wins / (teamStats?.record?.wins + teamStats?.record?.losses)) * 100).toFixed(0)
                          : 0}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recent Games */}
                <div className="bg-card border border-border rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-bold text-foreground uppercase">Games</h3>
                    <div className="flex items-center gap-3">
                      {/* Season Filter */}
                      {seasons.length > 0 && (
                        <select
                          value={selectedSeasonId || ''}
                          onChange={(e) => setSelectedSeasonId(e.target.value || null)}
                          className="text-sm bg-muted border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">All Seasons</option>
                          {seasons.map(season => (
                            <option key={season.id} value={season.id}>
                              {season.name} {season.is_active ? '(Active)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      <Link 
                        to={`/coach/teams/${selectedTeam.id}/games`}
                        className="text-sm text-primary font-medium hover:underline"
                      >
                        View All
                      </Link>
                    </div>
                  </div>
                  
                  {teamStats?.recent_games?.length > 0 ? (
                    <div className="space-y-3">
                      {teamStats.recent_games.slice(0, 5).map((game, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-10 rounded-full ${game.result === 'win' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <div>
                              <p className="font-medium text-foreground">
                                vs {game.opponent}
                                <span className={`ml-2 text-sm ${game.result === 'win' ? 'text-emerald-500' : 'text-red-500'}`}>
                                  {game.result === 'win' ? 'W' : 'L'}
                                </span>
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(game.game_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-heading text-xl font-bold text-foreground">
                              {game.team_score} - {game.opponent_score}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No games logged yet</p>
                      <Link to={`/coach/teams/${selectedTeam.id}/game`}>
                        <Button variant="outline" className="mt-4 gap-2">
                          <Plus size={16} />
                          Log First Game
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Team Player Stats */}
                <div className="bg-card border border-border rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-bold text-foreground uppercase">Player Stats</h3>
                    <Link 
                      to={`/coach/teams/${selectedTeam.id}/compare`}
                      className="text-sm text-primary font-medium hover:underline flex items-center gap-1"
                    >
                      <BarChart3 size={14} />
                      Compare Players
                    </Link>
                  </div>
                  
                  {teamStats?.player_stats?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3 text-xs text-muted-foreground uppercase">Player</th>
                            <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase">GP</th>
                            <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase">PPG</th>
                            <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase">RPG</th>
                            <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase">APG</th>
                            <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase">FG%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamStats.player_stats.map((player, i) => (
                            <tr key={i} className="border-b border-border/50 hover:bg-muted/50">
                              <td className="py-3 px-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-xs font-bold text-primary">
                                      {player.name?.charAt(0) || '?'}
                                    </span>
                                  </div>
                                  <span className="font-medium text-foreground">{player.name}</span>
                                </div>
                              </td>
                              <td className="text-center py-3 px-3 text-foreground">{player.games || 0}</td>
                              <td className="text-center py-3 px-3 font-bold text-foreground">{player.ppg?.toFixed(1) || 0}</td>
                              <td className="text-center py-3 px-3 text-foreground">{player.rpg?.toFixed(1) || 0}</td>
                              <td className="text-center py-3 px-3 text-foreground">{player.apg?.toFixed(1) || 0}</td>
                              <td className="text-center py-3 px-3 text-foreground">{player.fg_pct?.toFixed(0) || 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center py-4 text-muted-foreground">No player stats available</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* No Teams - Create First Team CTA */
          <div className="bg-card border border-border rounded-2xl p-8 text-center">
            <Trophy size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-bold text-foreground mb-2">Create Your First Team</h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              Manage multiple teams with season records, game logging, and player analytics.
            </p>
            <Link to="/coach/teams">
              <Button className="bg-primary gap-2">
                <Plus size={18} />
                Create Team
              </Button>
            </Link>
          </div>
        )}

        {/* All Players Section */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-foreground uppercase">All Players</h2>
            <Link 
              to="/coach/my-players"
              className="text-sm text-primary font-medium flex items-center gap-1 hover:underline"
            >
              Manage Roster <ChevronRight size={16} />
            </Link>
          </div>
          
          {topPlayers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {topPlayers.map((player) => (
                <Link
                  key={player.id}
                  to={`/coach/player/${player.id}`}
                  className="flex items-center justify-between p-4 bg-muted rounded-xl hover:bg-muted/80 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">
                        {player.name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{player.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {player.shot_count || 0} shots • {player.game_count || 0} games
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users size={32} className="mx-auto text-muted-foreground mb-2 opacity-50" />
              <p className="text-muted-foreground mb-4">No players in your roster yet</p>
              <Link to="/coach/my-players">
                <Button variant="outline" className="gap-2">
                  <UserPlus size={16} />
                  Add Players
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Generate Report CTA */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 rounded-2xl p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                <FileText size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground">Performance Reports</h3>
                <p className="text-sm text-muted-foreground">Generate shareable reports for players and parents</p>
              </div>
            </div>
            <Link to="/coach/reports">
              <Button variant="outline" className="gap-2 border-primary/50 hover:bg-primary/10">
                <FileText size={16} />
                Generate Report
              </Button>
            </Link>
          </div>
        </div>

        {/* Vital Resources Section */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-foreground uppercase mb-4">Resources</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* RealSkill App Launch */}
            <a
              href="https://app.possibletraining.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 rounded-xl p-4 hover:border-primary/50 transition-colors group"
              data-testid="realskill-app-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Rocket size={20} className="text-primary" />
                </div>
                <div>
                  <span className="font-bold text-foreground">RealSkill App</span>
                  <p className="text-xs text-muted-foreground">Pro-Drill Library</p>
                </div>
              </div>
              <ExternalLink size={18} className="text-primary group-hover:translate-x-1 transition-transform" />
            </a>

            {/* Digital Lab */}
            <Link
              to="/digital-lab"
              className="flex items-center justify-between bg-gradient-to-r from-white/20 to-white/10 border border-white/30 rounded-xl p-4 hover:border-white/50 transition-colors group"
              data-testid="digital-lab-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Crown size={20} className="text-white" />
                </div>
                <div>
                  <span className="font-bold text-foreground">The Digital Lab</span>
                  <p className="text-xs text-muted-foreground">Complete package</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-white group-hover:translate-x-1 transition-transform" />
            </Link>

            {/* Skool Community */}
            <a
              href="https://www.skool.com/skill-domination-x-4472"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between bg-gradient-to-r from-amber-500/20 to-amber-500/10 border border-amber-500/30 rounded-xl p-4 hover:border-amber-500/50 transition-colors group"
              data-testid="skool-community-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-amber-400" />
                </div>
                <div>
                  <span className="font-bold text-foreground">Skool Community</span>
                  <p className="text-xs text-muted-foreground">Connect with coaches</p>
                </div>
              </div>
              <ExternalLink size={18} className="text-amber-400 group-hover:translate-x-1 transition-transform" />
            </a>

            {/* Get RealSkill Info */}
            <Link
              to="/realskill-app"
              className="flex items-center justify-between bg-gradient-to-r from-purple-500/20 to-purple-500/10 border border-purple-500/30 rounded-xl p-4 hover:border-purple-500/50 transition-colors group"
              data-testid="realskill-info-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Rocket size={20} className="text-purple-400" />
                </div>
                <div>
                  <span className="font-bold text-foreground">Get RealSkill</span>
                  <p className="text-xs text-muted-foreground">$49.99/mo - Drills only</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-purple-400 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Admin Quick Link */}
        {user?.is_admin && (
          <Link
            to="/admin/analytics"
            className="flex items-center justify-between bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <TrendingUp size={20} className="text-primary" />
              <span className="font-medium text-foreground">Platform Analytics</span>
            </div>
            <ChevronRight size={18} className="text-muted-foreground" />
          </Link>
        )}
      </div>
    </Layout>
  );
};

export default CoachDashboard;
